import { useEffect, useState } from 'react';
import useLongPress from 'react-use/lib/useLongPress';

interface UseLongPressMessageOptions {
    enabled?: boolean;
    delay?: number;
    // Array of CSS class names or refs that should not trigger click outside
    // Can include the main element ref and any other exclusion targets
    excludeTargets?: (string | React.RefObject<HTMLElement>)[];
}

export const useLongPressMessage = ({
    enabled = true,
    delay = 300,
    excludeTargets = [],
}: UseLongPressMessageOptions = {}) => {
    const [isLongPressed, setIsLongPressed] = useState(false);

    const longPressEvents = useLongPress(
        () => {
            if (enabled) {
                setIsLongPressed((cur) => !cur);
            }
        },
        { delay },
    );

    useEffect(() => {
        if (!isLongPressed || !enabled) return;

        const handleClickOutside = (event: Event) => {
            if (event.target instanceof Element && excludeTargets.length > 0) {
                const isExcludedTarget = excludeTargets.some((target) => {
                    // Handle ref objects
                    if (typeof target === 'object' && target.current) {
                        return target.current.contains(event.target as Node);
                    }

                    // Handle class names, with or without dot
                    if (typeof target === 'string') {
                        const selector = target.startsWith('.') ? target : `.${target}`;
                        return !!(event.target as Element).closest?.(selector);
                    }

                    return false;
                });

                if (isExcludedTarget) return;
            }

            setIsLongPressed(false);
        };

        document.addEventListener('touchstart', handleClickOutside);
        return () => {
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [isLongPressed, enabled, excludeTargets]);

    return {
        isLongPressed,
        longPressEvents: enabled
            ? longPressEvents
            : {
                  onMouseDown: () => {},
                  onTouchStart: () => {},
                  onMouseUp: () => {},
                  onMouseLeave: () => {},
                  onTouchEnd: () => {},
              },
        setIsLongPressed,
    };
};
